import { ReactElement, useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { navigateToError } from '@utils';
import { FormType } from '@constants/forms';
import { ExpandableTableAccessible, FormLoading } from '@components/index';
import { iInstalmentFormValues } from '@services/InstalmentService';

import { iObligation } from '@interfaces/interfaces';
import { getObligationOptions } from '@api';
import styles from './InstalmentStep5.module.scss';

function InstalmentStep5(): ReactElement {
  const [loading, setLoading] = useState(false);
  const context = useFormikContext();
  const { values }: { values: iInstalmentFormValues } = useFormikContext();

  // Authorised Obligation
  const [authorisedOb, setObligationDetails] = useState({} as iObligation);
  // Already Payment Arranged Obligations
  const [arrangedObs, setArrangedObligations] = useState({} as { [PANumber: string]: iObligation[] });
  // Eligible Obligations
  const [eligibleObs, setEligibleObligations] = useState([] as iObligation[]);
  // Ineligible Obligations
  const [ineligibleObs, setIneligibleObligations] = useState([] as iObligation[]);
  // Total Payment Arrangement Amount
  const [defaultPAAmount, setDeafultPAAmount] = useState(0);
  // Other Ineligible Obligations Number
  const [additionalObligations, setAdditionalObligations] = useState(0);

  useEffect(() => {
    try {
      const obNumFormIk = values.OBLIGATION_NUMBER;
      if (values.OBLIGATION_NUMBER) {
      setLoading(true);
      getObligationOptions(obNumFormIk, FormType.INSTALMENT)
        .then((responseBody) => {
          if (responseBody && responseBody?.Authorised) {
            setObligationDetails(responseBody?.Authorised);
            context.setFieldValue('OBLIGATION_NUMBER', responseBody?.Authorised.Number);
            context.setFieldValue('AuthorisedObligation', responseBody?.Authorised);
            context.setFieldValue('InfringementNoticeNumber', responseBody?.Authorised.InfringementNumber);
            if (responseBody?.Authorised.DebtorType === 'Individual' && !values.isDebtorTypeIndividual) {
              context.setFieldValue('isDebtorTypeIndividual', true);
            }
            if (responseBody?.Authorised.DebtorType === 'Company' && !values.isDebtorTypeCompany) {
              context.setFieldValue('isDebtorTypeCompany', true);
            }
          }
          if (responseBody && responseBody?.Obligations) {
            setEligibleObligations(responseBody.Obligations.filter((ob) => !ob.IsOnPlan));
            const arrangedObs1 = responseBody.Obligations.filter((ob) => ob.IsOnPlan);
            let totalPAAmount = parseFloat(responseBody.Authorised.AmountDue);
            const PANumberMap = arrangedObs1.reduce((map, ob) => {
              totalPAAmount += parseFloat(ob.AmountDue);
              if (!map[ob.PANumber]) {
                  // eslint-disable-next-line no-param-reassign
                  map[ob.PANumber] = [];
              }
              map[ob.PANumber].push(ob);
              return map;
            }, {} as { [PANumber: string]: iObligation[] });
            setDeafultPAAmount(totalPAAmount);
            if (values.SelectedObligationsStep5.length > 0) {
              const eligibleObsMap = responseBody.Obligations.filter((ob) => !ob.IsOnPlan).filter((ob) => !ob.IsOnPlan).reduce((map, item) => {
                // eslint-disable-next-line no-param-reassign
                map[item.Number] = (parseFloat(item.AmountDue));
                return map;
              }, {} as {[obligationNo: string]: number});
              totalPAAmount += values.SelectedObligationsStep5.filter((item) => item.checked).reduce((sum, item) => (sum + eligibleObsMap[item.number]), 0);
            } else {
              responseBody.Obligations.filter((ob) => !ob.IsOnPlan).forEach((val) => { totalPAAmount += parseFloat(val.AmountDue); });
            }
            context.setFieldValue('TotalPaymentArrangementAmount', ((totalPAAmount.toString())));
            context.setFieldValue('TotalPaymentArrangementAmountDisplay', ((totalPAAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }))));
            setArrangedObligations(PANumberMap);
            setIneligibleObligations(responseBody.ObligationsInvalidTypes);
            setAdditionalObligations(responseBody.TotalAmount - responseBody.Obligations.length - responseBody.ObligationsInvalidTypes.length - 1);

            context.setFieldValue('AuthorisedObligation', responseBody?.Authorised);
            context.setFieldValue('EligibleObligations', responseBody?.Obligations);
            context.setFieldValue('IneligibleObligations', responseBody.ObligationsInvalidTypes);
            context.setFieldValue('ArrangedObligations', PANumberMap);
            context.setFieldValue('AdditionalObligationsNum', responseBody.TotalAmount - responseBody.Obligations.length - responseBody.ObligationsInvalidTypes.length - 1);
            setLoading(false);
          }
        })
        .catch((e) => console.log(e));
      }
    } catch (ex) {
      navigateToError();
    }
    return () => {
      setLoading(false);
    };
  }, [values.OBLIGATION_NUMBER]);

  useEffect(() => {
    const eligibleObsMap = eligibleObs.filter((ob) => !ob.IsOnPlan).reduce((map, item) => {
      // eslint-disable-next-line no-param-reassign
      map[item.Number] = (parseFloat(item.AmountDue));
      return map;
    }, {} as {[obligationNo: string]: number});
    let total = values.SelectedObligationsStep5.filter((item) => item.checked).reduce((sum, item) => (sum + eligibleObsMap[item.number]), 0);
    total += defaultPAAmount;
    context.setFieldValue('TotalPaymentArrangementAmount', total.toString());
    context.setFieldValue('TotalPaymentArrangementAmountDisplay', ((total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })));
  }, [values.SelectedObligationsStep5]);
  return (
    <>
      {loading && <FormLoading />}
      {!loading && (
        <>
          <div className="vfH2">Your fines</div>
          <p>You are applying for a payment arrangement for the following fine:</p>
          <div className={styles.offenceDetails}>
            <div className={styles.detailHeadings}>
              {authorisedOb.Number && <span>Obligation #</span>}
              {authorisedOb.OffenceDate && <span>Offence Date</span>}
              {authorisedOb.OffenceDesc && <span>Offence Description</span>}
              {authorisedOb.AmountDue && <span>Amount</span>}
            </div>
            <div className={styles.detailValues}>
              {authorisedOb.Number && <span className={styles.obligationNumber}>{authorisedOb.Number}</span>}
              {authorisedOb.OffenceDate && <span>{authorisedOb.OffenceDate}</span>}
              {authorisedOb.OffenceDesc && <span>{authorisedOb.OffenceDesc}</span>}
              {authorisedOb.AmountDue
              && (
              <span>
                $
                {authorisedOb.AmountDue}
              </span>
              )}
            </div>
            <div className={styles.detailTable}>
              <table className={styles.offenceTable}>
                <tbody>
                  {
                  authorisedOb.Number && (
                  <tr className={styles.onlyMobileRows}>
                    <td className={styles.tdFirstColBackground}>Obligation #</td>
                    <td className={styles.tdSecondColBackground}>{authorisedOb.Number}</td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.OffenceDate && (
                  <tr className={styles.onlyMobileRows}>
                    <td className={styles.tdFirstColBackground}>Offence Date</td>
                    <td className={styles.tdSecondColBackground}>{authorisedOb.OffenceDate}</td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.OffenceDesc && (
                  <tr className={styles.onlyMobileRows}>
                    <td className={styles.tdFirstColBackground}>Offence Description</td>
                    <td className={styles.tdSecondColBackground}>{authorisedOb.OffenceDesc}</td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.AmountDue && (
                  <tr className={styles.onlyMobileRows}>
                    <td className={styles.tdFirstColBackground}>Amount</td>
                    <td className={styles.tdSecondColBackground}>
                      $
                      {authorisedOb.AmountDue}
                    </td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.OffenceLocation && (
                  <tr className={styles.hideInMobileView}>
                    <td>Offence Location</td>
                    <td className={styles.uppcaseText}>{authorisedOb.OffenceLocation}</td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.OffenceTime && (
                  <tr className={styles.hideInMobileView}>
                    <td>Time of Offence</td>
                    <td>{authorisedOb.OffenceTime}</td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.Registration && (
                  <tr className={styles.hideInMobileView}>
                    <td>Vehicle Registration</td>
                    <td>{authorisedOb.Registration}</td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.OffenceLocation && (
                  <tr className={styles.onlyMobileRows}>
                    <td colSpan={2}>
                      Offence Location
                      <br />
                      <span className={styles.tdSpanFont}>{authorisedOb.OffenceLocation}</span>
                    </td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.OffenceTime && (
                  <tr className={styles.onlyMobileRows}>
                    <td colSpan={2}>
                      Time of Offence
                      <br />
                      <span className={styles.tdSpanFont}>{authorisedOb.OffenceTime}</span>
                    </td>
                  </tr>
                  )
                  }
                  {
                  authorisedOb.Registration && (
                  <tr className={styles.onlyMobileRows}>
                    <td colSpan={2}>
                      Vehicle Registration
                      <br />
                      <span className={styles.tdSpanFont}>{authorisedOb.Registration}</span>
                    </td>
                  </tr>
                  )
                  }
                </tbody>
              </table>
            </div>
            <br />
            {eligibleObs.length !== 0 && (
              <>
                <p>
                  We noticed you have other eligible fines and they have been included in this application. Please de-select any fine(s) you
                  {' '}
                  <strong>do not</strong>
                  {' '}
                  want to include. Please note, if you do not deal with your outstanding fines, enforcement action may be taken against you.
                </p>
                <Field
                  name="SelectedObligationsStep5"
                  key="eligible"
                  obligations={eligibleObs}
                  inactiveRows={false}
                  component={ExpandableTableAccessible}
                  selectedRows />
                <br />
              </>
            )}
            {Object.entries(arrangedObs).map(([PANumber, obligations]) => (
              <>
                <p>
                  You also have the following fines, which are currently on
                  {' '}
                  <strong>
                    payment arrangement #
                    {PANumber}
                  </strong>
                  . These will be included in your new payment arrangement.
                </p>
                <Field
                  name="SelectedObligationsStep5"
                  key={PANumber}
                  obligations={obligations}
                  inactiveRows
                  component={ExpandableTableAccessible}
                  selectedRows
                />
                <br />
              </>
            ))}
            {ineligibleObs.length !== 0 && values.isDebtorTypeIndividual && (
              <>
                <p>
                  You also have the following matters, which
                  {' '}
                  <strong>cannot</strong>
                  {' '}
                  be included in a payment arrangement. Please contact Fines Victoria to pay or discuss your options.
                  For information on eligiblity, visit
                  {' '}
                  <a href="/Your-options/Request-to-pay-by-instalments" target="_blank" rel="noreferrer" aria-label="Payment Arrangement">Payment Arrangements.</a>
                </p>
                <Field
                  key="ineligible"
                  obligations={ineligibleObs}
                  inactiveRows
                  selectedRows={false}
                  name="SelectedObligationsStep5"
                  component={ExpandableTableAccessible}
                />
                <br />
              </>
              )}
            {ineligibleObs.length !== 0 && values.isDebtorTypeCompany && (
              <>
                <p>
                  You also have the following matters, which
                  {' '}
                  <strong>cannot</strong>
                  {' '}
                  be included in a payment arrangement. Please contact Fines Victoria to pay or discuss any options.
                  For information on eligiblity, visit
                  {' '}
                  <a href="/Your-options/Request-to-pay-by-instalments" target="_blank" rel="noreferrer" aria-label="Payment Arrangement">Payment Arrangements.</a>
                </p>
                <Field
                  key="ineligible"
                  obligations={ineligibleObs}
                  inactiveRows
                  selectedRows={false}
                  name="SelectedObligationsStep5"
                  component={ExpandableTableAccessible}
                />
                <br />
              </>
              )}

            <p className={styles.middleNoteSection}>This summary may not be a complete listing of your fines. Infringement information may vary as a result of a difference in:</p>
            <div>
              <ul>
                <li>the spelling of your name</li>
                <li>your address</li>
                <li>your driver licence number</li>
                {values.isDebtorTypeCompany && <li>your registration number</li>}
                <br />
              </ul>
            </div>
            { additionalObligations > 0
            && (
              <p>
                If you have fines issued by a court, and they are in a separate payment arrangement with terms set by that court, then you will not be able to include them in this payment arrangement.
              </p>
            )}
            <p>
              The total value of your repayments will be
              {' '}
              <strong>
                $
                {values.TotalPaymentArrangementAmountDisplay}
              </strong>
              {' '}
            </p>
          </div>
        </>
      )}
    </>
  );
}

export default InstalmentStep5;
